import React, { useRef, useState, useCallback } from 'react';

function formatBytes(bytes) {
  if (!bytes) return '0KB';
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(i > 0 && n < 10 ? 1 : 0)}${units[i]}`;
}

/**
 * 공통 멀티 파일 업로드 컴포넌트.
 * - 드래그앤드롭 + 클릭하여 찾아보기 모두 지원
 * - 파일 개수/개별 파일 용량 제한, 중복 파일 방지
 * - 제어 컴포넌트: files(File[])/onChange(File[]) 를 부모가 상태로 들고 있는다.
 *   (실제 업로드/서버 전송은 상위 화면에서 FormData 등으로 처리)
 *
 * props:
 *  - files: File[]
 *  - onChange: (files: File[]) => void
 *  - accept?: string (예: "image/*,.pdf")
 *  - maxFiles?: number (기본 10)
 *  - maxSizeMB?: number (파일당 제한, 기본 20)
 *  - disabled?: boolean
 */
export default function MultiFileUpload({
  files = [], onChange, accept, maxFiles = 10, maxSizeMB = 20, disabled = false,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');

  const addFiles = useCallback((incoming) => {
    setError('');
    const incomingArr = Array.from(incoming);
    const next = [...files];
    let rejected = '';

    for (const f of incomingArr) {
      if (next.length >= maxFiles) {
        rejected = `최대 ${maxFiles}개까지 첨부할 수 있습니다.`;
        break;
      }
      if (f.size > maxSizeMB * 1024 * 1024) {
        rejected = `'${f.name}' 파일이 ${maxSizeMB}MB 제한을 초과했습니다.`;
        continue;
      }
      if (next.some((existing) => existing.name === f.name && existing.size === f.size)) {
        continue;
      }
      next.push(f);
    }

    if (rejected) setError(rejected);
    onChange(next);
  }, [files, maxFiles, maxSizeMB, onChange]);

  const handleInputChange = (e) => {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const removeFile = (index) => {
    onChange(files.filter((_, i) => i !== index));
  };

  return (
    <div className="file-upload">
      <div
        className={`file-drop-zone${dragOver ? ' drag-over' : ''}${disabled ? ' disabled' : ''}`}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) inputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        role="button"
        tabIndex={disabled ? -1 : 0}
      >
        <div className="file-drop-title">파일을 끌어다 놓거나 클릭하여 업로드</div>
        <div className="file-drop-sub">최대 {maxFiles}개 · 파일당 {maxSizeMB}MB 이하</div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          hidden
          disabled={disabled}
          onChange={handleInputChange}
        />
      </div>

      {error && <div className="file-upload-error">{error}</div>}

      {files.length > 0 && (
        <ul className="file-list">
          {files.map((f, i) => (
            <li key={`${f.name}-${f.size}-${i}`} className="file-list-item">
              <span className="file-ext">{(f.name.split('.').pop() || '').slice(0, 4).toUpperCase()}</span>
              <span className="file-name" title={f.name}>{f.name}</span>
              <span className="file-size">{formatBytes(f.size)}</span>
              <button type="button" className="file-remove" onClick={() => removeFile(i)} aria-label={`${f.name} 제거`}>&times;</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
